import { useState } from 'react';
import { Typography, Space, Alert, Spin, Divider } from 'antd';
import { MetricCardGrid } from '../components/dashboard/MetricCardGrid';
import { DashboardCharts } from '../components/dashboard/DashboardCharts';
import { TimeRangeSelector } from '../components/dashboard/TimeRangeSelector';
import { useMetrics } from '../api/queries/useMetrics';
import { useCharts } from '../api/queries/useCharts';
import { useTenant } from '../hooks/useTenant';
import type { TimeRange } from '../api/types';
import { getErrorMessage } from '../utils/error';

const { Title } = Typography;

export function DashboardPage() {
  const [timeRange, setTimeRange] = useState<TimeRange>('24h');
  const { tenantId, isLoading: isTenantLoading } = useTenant();
  const { data: metrics, isLoading: isMetricsLoading, error: metricsError, dataUpdatedAt } = useMetrics(timeRange);
  const { data: charts, isLoading: isChartsLoading, error: chartsError } = useCharts(timeRange);

  if (isTenantLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
        <p>Loading tenants...</p>
      </div>
    );
  }

  if (!tenantId) {
    return (
      <Alert
        type="warning"
        message="No Tenant Selected"
        description="Please select a tenant from the dropdown in the header to view metrics."
        showIcon
      />
    );
  }

  const lastUpdated = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString()
    : null;

  return (
    <div>
      <Space
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: 24,
        }}
      >
        <Title level={3} style={{ margin: 0 }}>
          Dashboard
        </Title>
        <Space>
          {lastUpdated && (
            <span style={{ color: '#999', fontSize: 12 }}>
              Last updated: {lastUpdated}
            </span>
          )}
          <TimeRangeSelector value={timeRange} onChange={setTimeRange} />
        </Space>
      </Space>

      {metricsError && (
        <Alert
          type="error"
          message="Error loading metrics"
          description={getErrorMessage(metricsError)}
          style={{ marginBottom: 16 }}
          showIcon
        />
      )}

      {chartsError && (
        <Alert
          type="error"
          message="Error loading charts"
          description={getErrorMessage(chartsError)}
          style={{ marginBottom: 16 }}
          showIcon
        />
      )}

      <MetricCardGrid metrics={metrics} loading={isMetricsLoading} />

      <Divider />

      <DashboardCharts
        metrics={metrics}
        charts={charts}
        loading={isMetricsLoading || isChartsLoading}
      />
    </div>
  );
}
